import {
  ElectionEstimate,
  ElectionSaved,
  MyElectionPreview,
  MyReward,
  MyRewards,
  type ElectionSelection,
  type ReleaseAwardEarly,
  type SaveElection,
} from '@myrewards/contracts';
import { queryOptions, skipToken } from '@tanstack/react-query';
import { request } from './client';

export type RewardsView = 'awards' | 'elections' | 'documents';
export interface RewardsPageOptions {
  view?: RewardsView;
  page?: number;
  size?: number;
}

const linePath = (requestId: string, lineId: string): string =>
  `/api/v1/awards/${encodeURIComponent(requestId)}/lines/${encodeURIComponent(lineId)}`;

/** Own visible lines only; never reuse the staff award response or its query keys. */
export function rewardsPageQuery({ page = 1, size, view = 'awards' }: RewardsPageOptions = {}) {
  const query = new URLSearchParams({ view, page: String(page) });
  if (size !== undefined) query.set('size', String(size));
  return queryOptions({
    queryKey: ['my-rewards', 'page', { page, size, view }] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/my-rewards?${query.toString()}`, { schema: MyRewards, signal }),
    staleTime: 10_000,
  });
}

/** Today's election window, reminders and share price, for the Elections tab's preview (E3). */
export const myElectionPreviewQuery = queryOptions({
  queryKey: ['my-rewards', 'election-facts'] as const,
  queryFn: ({ signal }) =>
    request('/api/v1/my-rewards/election-preview', { schema: MyElectionPreview, signal }),
  staleTime: 60_000,
});

export function rewardQuery(lineId: string) {
  return queryOptions({
    queryKey: ['my-rewards', 'one', lineId] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/my-rewards/${encodeURIComponent(lineId)}`, { schema: MyReward, signal }),
    staleTime: 10_000,
  });
}

/** Every selection gets its own server estimate; no prior choice is shown as its placeholder. */
export function electionPreviewQuery(
  requestId: string,
  lineId: string,
  selection: ElectionSelection | null,
) {
  return queryOptions({
    queryKey: ['my-rewards', 'election-preview', requestId, lineId, selection] as const,
    queryFn:
      selection === null
        ? skipToken
        : ({ signal }) =>
            request(`${linePath(requestId, lineId)}/election/preview`, {
              method: 'POST',
              body: selection,
              schema: ElectionEstimate,
              signal,
            }),
    enabled: selection !== null,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    meta: { busy: true },
  });
}

export function saveElection(
  requestId: string,
  lineId: string,
  body: SaveElection,
): Promise<ElectionSaved> {
  return request(`${linePath(requestId, lineId)}/election`, {
    method: 'POST',
    body,
    schema: ElectionSaved,
  });
}

/** Changes employee visibility; it is not a physical handout acknowledgment. */
export function releaseRewardEarly(
  requestId: string,
  lineId: string,
  body: ReleaseAwardEarly,
): Promise<ElectionSaved> {
  return request(`${linePath(requestId, lineId)}/release-early`, {
    method: 'POST',
    body,
    schema: ElectionSaved,
  });
}
